import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import * as i18n from "util/i18n";

import { selectors as investmentfundsSelectors } from "reducers/investmentfunds";
import Container from "pages/_components/Container";
import CustomText from "pages/_components/CustomText/CustomText";
import Button from "pages/_components/Button";
import "../../../styles/components/modules/_DashboardCard.scss";

const EPS = 0.005; // umbral para evitar falsos ceros por flotantes

const Fci = ({ fci, currency, dollarRate }) => {
    const dispatch = useDispatch();

    // Normalizo a array
    const accounts = Array.isArray(fci) ? fci : [];

    // Helper: últimos 5 dígitos sin ceros a la izquierda
    const last5 = (accNumber = "") => {
        const raw = String(accNumber || "").replace(/^0+/, "");
        const chunk = raw.slice(-5) || raw;
        return chunk.padStart(5, "•");
    };

    // Agrupar por cuenta, filtrar especies con monto > 0 y cuentas con total > 0
    // Agrupar por cuenta, filtrar especies con monto > 0 y cuentas con total > 0
    const { perAccount, grandTotal, isEmpty } = useMemo(() => {
        let fx = 1;

        if (currency === "USD") {
            fx = dollarRate > 0 ? dollarRate : 1;
        }

        const perAccRaw = accounts.map((acc) => {
            const holdings = Array.isArray(acc.listaPorEspecie) ? acc.listaPorEspecie : [];

            const itemsAll = holdings
                .map((item) => {
                    const montoARS = Number(item?.saldo?.enPesos) || 0;
                    const monto = currency === "ARS" ? montoARS : montoARS / fx;
                    return {
                        codigo: item?.especie?.codigo,
                        descripcion: item?.especie?.descripcion,
                        monto,
                    };
                })
                .filter((x) => (x?.monto || 0) > EPS); // solo especies con valor

            const total = itemsAll.reduce((sum, it) => sum + (it.monto || 0), 0);

            return {
                accountNumber: acc.accountNumber,
                label: `Cuenta Comitente (${last5(acc.accountNumber)})`,
                itemsAll,
                total,
            };
        });

        // Filtrar cuentas con total > EPS
        const perAcc = perAccRaw.filter((a) => (a.total || 0) > EPS);

        // Total global
        const grand = perAcc.reduce((s, a) => s + (a.total || 0), 0);

        return { perAccount: perAcc, grandTotal: grand, isEmpty: grand <= EPS };
    }, [accounts, currency, dollarRate]);

    // Formatter moneda
    const formatter = (value) =>
        Number(value || 0).toLocaleString("es-AR", {
            style: "currency",
            currency: currency === "ARS" ? "ARS" : "USD",
            minimumFractionDigits: 2,
        });

    return (
        <div className="above-the-fold">
            <Container className="container--layout flex-grow">
                <div className={`card card--fci ${isEmpty ? "card--disabled" : ""}`}>
                    {/* Header */}
                    <div className="card__header">
                        <div>
                            <CustomText
                                text="investmentFunds.dashboard.FCI"
                                textType="I18N"
                                className="card__header-title"
                            />
                        </div>
                        {!isEmpty && (
                            <Button
                                bsStyle="link"
                                className="card__header-action"
                                onClick={() => dispatch(push("/investmentFunds/fci/list"))}
                                disabled={isEmpty}>
                                <CustomText text="global.showMore" textType="I18N" />
                                <span className="card__header-icon">→</span>
                            </Button>
                        )}
                    </div>

                    {isEmpty ? (
                        <div className="card__empty-message">
                            <CustomText
                                text={i18n.get(
                                    "investmentFunds.dashboard.noInvest.FCI",
                                    "Actualmente, no posees Fondos Comunes de Inversión",
                                )}
                                textType="plain"
                                className="card__empty-text"
                            />
                        </div>
                    ) : (
                        <>
                            {/* Columnas por cuenta */}
                            <div className="card__columns">
                                {perAccount.map((acc) => (
                                    <div className="card__column" key={acc.accountNumber || acc.label}>
                                        <div className="card__column-title">
                                            <CustomText text={acc.label} textType="plain" />
                                        </div>

                                        <ul className="card__list">
                                            {acc.itemsAll.slice(0, 4).map(({ codigo, descripcion }) => (
                                                <li key={`${acc.accountNumber}-${codigo}`} className="card__list-item">
                                                    <span className="card__bullet card__bullet--fci" />
                                                    <CustomText text={descripcion} className="card__text" />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* Total combinado (suma de TODOS los ítems con monto > 0) */}
                            <div className="card__total">
                                <CustomText
                                    text="investmentFunds.dashboard.FCI.totalAmount"
                                    textType="I18N"
                                    className="card__total-label"
                                />
                                <CustomText
                                    text={formatter(grandTotal)}
                                    textType="plain"
                                    className="card__total-value"
                                />
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </div>
    );
};

Fci.propTypes = {
    fci: PropTypes.arrayOf(
        PropTypes.shape({
            accountNumber: PropTypes.string,
            listaPorEspecie: PropTypes.arrayOf(
                PropTypes.shape({
                    especie: PropTypes.shape({
                        codigo: PropTypes.string,
                        descripcion: PropTypes.string,
                    }),
                    saldo: PropTypes.shape({
                        enPesos: PropTypes.number,
                    }),
                }),
            ),
        }),
    ),
    currency: PropTypes.oneOf(["ARS", "USD"]).isRequired,
    dollarRate: PropTypes.number, // lo cuidamos igual en runtime
};

Fci.defaultProps = {
    fci: [],
    dollarRate: 0,
};

const mapStateToProps = (state) => ({
    fci: investmentfundsSelectors.getFci(state) || [],
});

export default connect(mapStateToProps)(Fci);
